// frontend/src/components/public/blocks/PastImpactBlock.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Grid, Chip } from '@mui/material';
import { TrendingUp as TrendingIcon } from '@mui/icons-material';

interface PastImpactBlockProps {
  data: {
    year: string;
    metrics: Array<{
      label: string;
      value: number;
    }>;
    featuredArtists: string[];
    satisfactionRate: {
      percentage: number;
      source: string;
    };
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const PastImpactBlock: React.FC<PastImpactBlockProps> = ({ data, theme }) => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );

    if (blockRef.current) {
      observer.observe(blockRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <Box
      ref={blockRef}
      sx={{
        py: 8,
        px: 3,
        bgcolor: '#f9f9f9'
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: theme?.primaryColor || '#2C3E50',
              mb: 1
            }}
          >
            {data.year} Season Highlights
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            A year of exceptional performances and impact
          </Typography>
        </Box>

        {/* Metrics Grid */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          {data.metrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 3,
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
                  }
                }}
              >
                <AnimatedNumber
                  value={metric.value}
                  animate={hasAnimated}
                  duration={2000}
                  sx={{
                    fontSize: '3rem',
                    fontWeight: 700,
                    color: theme?.primaryColor || '#3A9188',
                    lineHeight: 1.2,
                    mb: 1
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}
                >
                  {metric.label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Featured Artists */}
        {data.featuredArtists.length > 0 && (
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: '#2C3E50',
                mb: 3
              }}
            >
              Featured Artists
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'center'
              }}
            >
              {data.featuredArtists.map((artist, index) => (
                <Chip
                  key={index}
                  label={artist}
                  sx={{
                    fontSize: '0.95rem',
                    py: 2.5,
                    px: 1,
                    bgcolor: 'white',
                    border: `1px solid ${theme?.primaryColor || '#3A9188'}`,
                    color: '#2C3E50',
                    '&:hover': {
                      bgcolor: theme?.accentColor || '#E8F5E9'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Satisfaction Rate */}
        {data.satisfactionRate.percentage > 0 && (
          <Box
            sx={{
              textAlign: 'center',
              p: 4,
              bgcolor: theme?.primaryColor || '#3A9188',
              borderRadius: 2,
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <TrendingIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                  <AnimatedNumber
                    value={data.satisfactionRate.percentage}
                    animate={hasAnimated}
                    duration={2000}
                    suffix="%"
                    sx={{ fontSize: '4rem', fontWeight: 700 }}
                  />
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Audience Satisfaction Rate
                </Typography>
                {data.satisfactionRate.source && (
                  <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                    Source: {data.satisfactionRate.source}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Animated Number Component
interface AnimatedNumberProps {
  value: number;
  animate: boolean;
  duration?: number;
  suffix?: string;
  sx?: any;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  animate,
  duration = 2000,
  suffix = '',
  sx
}) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (!animate) return;

    const startTime = Date.now();
    const endTime = startTime + duration;

    const updateValue = () => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = Math.floor(easeOutQuart * value);
      
      setDisplayValue(current);

      if (now < endTime) {
        requestAnimationFrame(updateValue);
      } else {
        setDisplayValue(value);
      }
    };

    requestAnimationFrame(updateValue);
  }, [animate, value, duration]);

  return (
    <Typography component="span" sx={sx}>
      {displayValue.toLocaleString()}{suffix}
    </Typography>
  );
};

export default PastImpactBlock;